<template>
  <div>
    <a-join-telegram
      v-if="showTelegramWidget"
      class="telegram-widget"
      :channel-type="telegramChannel"
    />

    <a-lazy-hydrate
      v-if="!hideMostViewedStrip && mostViewedArticles.length"
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.MOST_VIEWED
        )
      "
      when-visible
    >
      <a-most-viewed
        :articles="mostViewedArticles"
        :last-modified="mostViewedArticlesLastModified"
        title="Latest"
        @mounted="
          $_lazyHydratedComponentState_onHydratedComponentMounted(
            $options.consts.HYDRATED_SECTION_NAME.MOST_VIEWED
          )
        "
      />
    </a-lazy-hydrate>

    <a-categories-strip
      v-if="!hideCategoriesStrip"
      :page-type="pageType"
      :category-name="categoryName"
      :subcategory-name="subcategoryName"
      :categories-strip="categoriesStrip"
      :last-modified="categoriesStripLastModified"
    >
      <slot name="disclaimer" />
    </a-categories-strip>

    <div class="articles__wrapper">
      <a-article-list
        :articles="allArticles"
        id-field="id"
        class="infinite-news__article-list"
      >
        <template v-slot="{ article, position }">
          <a-lazy-hydrate
            when-visible
            :key="
              $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
                article.id
              )
            "
          >
            <a-news-item
              v-bind="{ [$options.consts.NEWS_ITEM_DATA_ATTRIBUTE]: true }"
              :article="article"
              :show-tag="showTag"
              :icon-max-space="iconMaxSpace"
              :icon-min-space="iconMinSpace"
              :hide-bottom-border="isBannerPlace(position)"
              :hide-additional-info="hideAdditionalInfo"
              :last-modified="article.LastModified"
              :title-html-tag="titleHtmlTag"
              :action-icons="actionIcons"
              :class="$options.consts.ARTICLE_SLOT_CLASS_NAME"
              @mounted="
                $_lazyHydratedComponentState_onHydratedComponentMounted(
                  article.id
                )
              "
              v-on="$listeners"
            >
              <template v-slot:bottom>
                <slot name="news-item-bottom" :article="article" />
              </template>
            </a-news-item>
          </a-lazy-hydrate>
        </template>
        <template v-slot:banner="{ position }">
          <a-banner
            v-if="isBannerPlace(position)"
            class="news__banner"
            :banner-settings="getBannerSettings(position)"
            use-random-banner-id
          />
        </template>
        <template v-slot:video="{ position }">
          <a-video-banner
            v-if="isVideoBannerPlace(position)"
            class="news-list__video-banner"
            :video-banner-type="currentVideoBannerType"
          />
        </template>
      </a-article-list>
      <a-see-more-less
        :key="
          $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
            $options.consts.HYDRATED_SECTION_NAME.SEE_MORE_LESS
          )
        "
        :page="pagination.page"
        :pages-count="pagesCount"
        :is-expanded="false"
        pagination
        :in-progress="inProgress"
        class="news-list__see-more-less"
        @mounted="
          $_lazyHydratedComponentState_onHydratedComponentMounted(
            $options.consts.HYDRATED_SECTION_NAME.SEE_MORE_LESS
          )
        "
        @input="getNextArticleChunk"
      />
      <p v-if="isNoNewsMessageVisible" class="news-list__no-news-found">
        No news found
      </p>
    </div>
  </div>
</template>

<script>
import { CHANNEL_TYPE } from 'shared/AJoinTelegram/enums'
import { SPECIAL_CATEGORY } from 'enums/special-categories'
import {
  propValidator,
  PROP_TYPES,
  isPaginationValid
} from '@fmpedia/validators'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { HTML_TAG } from '@/utils/helpers/processHtml/enums'
import AArticleList from 'shared/AArticleList'
import AMostViewed from 'shared/AMostViewed'
import ACategoriesStrip from 'shared/ACategoriesStrip'
import ANewsItem from 'shared/ANewsItem'
import { NEWS_ITEM_DATA_ATTRIBUTE } from 'enums/data-attributes'
import {
  VIDEO_BANNER_POSITION,
  VIDEO_BANNER_TYPE_BY_ROUTE_NAME,
  ARTICLE_SLOT_CLASS_NAME,
  BANNER_POSITION
} from './enums'
import {
  BANNER_COUNT_BY_NEWS_LIST_PAGE_NAME,
  BANNER_SETTINGS_BY_ROUTE_NAME
} from 'enums/banners/banner-settings-by-route-name'
import { getInsideListBannerSettingsKey } from 'enums/banners/banner-settings'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import mixins from '@/utils/mixins'

const HYDRATED_SECTION_NAME = {
  MOST_VIEWED: 'MOST_VIEWED',
  SEE_MORE_LESS: 'SEE_MORE_LESS'
}

export default {
  name: 'APaginatedNewsList',
  mixins: [hydrationHelpers, mixins.lazyHydratedComponentState],
  components: {
    AMostViewed,
    ACategoriesStrip,
    AJoinTelegram: () => import('shared/AJoinTelegram'),
    ASeeMoreLess: hydrateWhenVisible(() => import('shared/ASeeMoreLess'), {
      props: ['page', 'pages-count', 'is-expanded', 'pagination', 'in-progress']
    }),
    AArticleList,
    ANewsItem,
    ABanner: hydrateWhenVisible(() => import('shared/ABanner'), {
      props: ['banner-settings', 'use-random-banner-id']
    }),
    AVideoBanner: hydrateWhenVisible(() => import('shared/AVideoBanner'), {
      props: ['video-banner-type']
    })
  },
  props: {
    pagination: propValidator(
      [PROP_TYPES.OBJECT],
      true,
      null,
      isPaginationValid
    ),
    pageType: propValidator([PROP_TYPES.STRING], false),
    categoriesStrip: propValidator([PROP_TYPES.ARRAY], false, () => []),
    categoriesStripLastModified: propValidator([PROP_TYPES.NUMBER], false),
    categoryName: propValidator([PROP_TYPES.STRING], false, ''),
    subcategoryName: propValidator([PROP_TYPES.STRING], false, ''),
    allArticles: propValidator([PROP_TYPES.ARRAY]),
    pagesCount: propValidator([PROP_TYPES.STRING, PROP_TYPES.NUMBER], false),
    inProgress: propValidator([PROP_TYPES.BOOLEAN], false, false),
    showTelegramWidget: propValidator([PROP_TYPES.BOOLEAN], false, true),
    hideCategoriesStrip: propValidator([PROP_TYPES.BOOLEAN], false, false),
    hideMostViewedStrip: propValidator([PROP_TYPES.BOOLEAN], false, false),
    hideAdditionalInfo: propValidator([PROP_TYPES.BOOLEAN], false, false),
    showTag: propValidator([PROP_TYPES.BOOLEAN], false, false),
    mostViewedArticles: propValidator([PROP_TYPES.ARRAY], false, () => []),
    mostViewedArticlesLastModified: propValidator([PROP_TYPES.NUMBER], false),
    bannerPosition: propValidator([PROP_TYPES.NUMBER], false, BANNER_POSITION),
    titleHtmlTag: propValidator(
      [PROP_TYPES.STRING],
      false,
      HTML_TAG.HEADING_H3
    ),
    actionIcons: propValidator([PROP_TYPES.ARRAY], false),
    iconMaxSpace: propValidator([PROP_TYPES.NUMBER], false, 70),
    iconMinSpace: propValidator([PROP_TYPES.NUMBER], false, 35)
  },
  consts: {
    NEWS_ITEM_DATA_ATTRIBUTE,
    ARTICLE_SLOT_CLASS_NAME,
    HYDRATED_SECTION_NAME
  },
  computed: {
    telegramChannel() {
      return (this.$route.params.category || '').toLowerCase() ===
        SPECIAL_CATEGORY.CRYPTOCURRENCY
        ? CHANNEL_TYPE.CRYPTO
        : CHANNEL_TYPE.FM
    },
    isNoNewsMessageVisible() {
      return (
        !this.inProgress && this.allArticles && this.allArticles.length === 0
      )
    },
    currentVideoBannerType() {
      return VIDEO_BANNER_TYPE_BY_ROUTE_NAME[this.$route.name] || null
    }
  },
  methods: {
    rerenderNonHydratedComponents() {
      const componentIdentifiers = this.allArticles.map(article => article.id)
      this.$_lazyHydratedComponentState_rerenderNonHydratedComponents([
        ...componentIdentifiers,
        ...Object.values(HYDRATED_SECTION_NAME)
      ])
    },
    isBannerPlace(index) {
      const position = index + 1
      return position % this.bannerPosition === 0
    },
    isVideoBannerPlace(index) {
      const position = index + 1
      return this.currentVideoBannerType && position === VIDEO_BANNER_POSITION
    },
    getNextArticleChunk() {
      this.$emit('get-new-chunk')
    },
    getBannerSettings(index) {
      const position = index + 1

      if (!BANNER_COUNT_BY_NEWS_LIST_PAGE_NAME[this.$route.name]) return {}

      const { mobile, desktop } = BANNER_COUNT_BY_NEWS_LIST_PAGE_NAME[
        this.$route.name
      ]
      const bannersCount = this.$_hydrationHelpers_mobileOrDesktopValue(
        mobile,
        desktop
      )

      const bannerNumber =
        (position / this.bannerPosition) % bannersCount || bannersCount
      return BANNER_SETTINGS_BY_ROUTE_NAME[this.$route.name][
        getInsideListBannerSettingsKey(bannerNumber)
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
.articles__wrapper {
  position: relative;
  max-width: 1450px;
  padding-bottom: 50px;
  margin: 20px auto 0 auto;
  word-wrap: break-word;

  @include mobile {
    margin: 0 0 40px 0;
  }

  .news-list__no-news-found {
    text-align: center;
  }

  .news__banner {
    margin: 10px 0;

    @include mobile {
      margin-top: 0;
    }
  }

  .news-list__video-banner {
    margin: 30px 0 10px 0;

    /deep/ .primisslate {
      display: flex;
      max-width: 660px;
      margin: 0 auto;
    }
  }

  .news-list__see-more-less {
    margin-top: 20px;
  }
}

.telegram-widget {
  margin-bottom: 10px;
}
</style>
